import React from 'react';

export function RotationMockupGraphic() {
  const rows = [
    { role: 'Worship Lead',     name: 'Sarah M.',   status: '✓ Confirmed',    color: '#4ade80' },
    { role: 'Sound Tech',       name: 'James R.',   status: '✓ Confirmed',    color: '#4ade80' },
    { role: 'Greeters (×3)',    name: 'Auto-fill',  status: 'Notifying…',     color: '#fbbf24' },
    { role: "Children's Staff", name: 'Lisa T.',    status: '✓ Confirmed',    color: '#4ade80' },
    { role: 'Media / Slides',   name: 'Carlos D.',  status: '↻ Rescheduled',  color: '#818cf8' },
  ];
  return (
    <div className="w-full rounded-xl p-4" style={{ background: '#1e1b4b' }}>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-indigo-300 font-bold">⚡ Auto-Schedule · Apr 13</p>
        <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded-full">5 roles</span>
      </div>
      {rows.map(({ role, name, status, color }) => (
        <div
          key={role}
          className="flex justify-between items-center rounded-lg px-3 py-2 mb-1.5 text-xs"
          style={{ background: 'rgba(255,255,255,0.06)' }}
        >
          <div>
            <span className="text-indigo-100 font-medium">{role}</span>
            <span className="text-slate-400 ml-2">· {name}</span>
          </div>
          <span style={{ color }}>{status}</span>
        </div>
      ))}
      <p className="text-xs text-slate-500 mt-3 text-right">Scheduly · 0 gaps remaining</p>
    </div>
  );
}

export function WebsiteFirstGraphic() {
  const messages = [
    { from: 'Scheduly', text: "You're scheduled for Greeting on Sunday, Apr 13. Reply YES to confirm.", time: '10:02 AM', type: 'incoming' },
    { from: 'You', text: 'YES', time: '10:03 AM', type: 'outgoing' },
    { from: 'Scheduly', text: "Confirmed! You're all set for Greeting on Apr 13 at 9:00 AM.", time: '10:03 AM', type: 'incoming' },
  ];
  return (
    <div className="w-full rounded-xl p-4" style={{ background: '#0f172a' }}>
      <div className="flex justify-between items-center mb-3">
        <p className="text-xs text-teal-300 font-bold">📱 SMS Scheduling · No App Needed</p>
        <span className="text-xs bg-teal-600 text-white px-2 py-0.5 rounded-full">Auto-notify</span>
      </div>
      <div className="space-y-2">
        {messages.map(({ from, text, time, type }, i) => (
          <div
            key={i}
            className={`flex ${type === 'outgoing' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className="rounded-xl px-3 py-2 max-w-[80%]"
              style={{
                background: type === 'outgoing' ? '#4f46e5' : 'rgba(255,255,255,0.08)',
              }}
            >
              {type === 'incoming' && (
                <p className="text-[10px] text-teal-400 font-bold mb-0.5">{from}</p>
              )}
              <p className={`text-xs ${type === 'outgoing' ? 'text-white' : 'text-slate-200'}`}>
                {text}
              </p>
              <p className="text-[10px] text-slate-500 text-right mt-0.5">{time}</p>
            </div>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-500 mt-3 text-right">No download · No login · Just reply</p>
    </div>
  );
}

const blogPosts = [
  {
    slug: 'automate-church-volunteer-rotations',
    title: 'How Small Churches Save 3+ Hours a Week by Automating Volunteer Rotations',
    date: '2026-04-04',
    author: 'Scheduly Team',
    readTime: '5 min read',
    excerpt:
      'Tired of the Saturday-night scramble to find a sound tech? Here is how small churches are using automated rotations to reclaim hours every week — and keep their volunteers happy.',
    tags: ['Volunteer Management', 'Church Operations'],
    seo: {
      title:
        'How Small Churches Save 3+ Hours a Week by Automating Volunteer Rotations | Scheduly Blog',
      description:
        'Learn how small churches automate volunteer rotations to save time, reduce burnout, and keep every Sunday covered. Practical tips for church leaders.',
      canonical:
        'https://scheduly.org/blog/automate-church-volunteer-rotations',
      ogTitle:
        'How Small Churches Save 3+ Hours a Week by Automating Volunteer Rotations',
      ogDescription:
        'Tired of the Saturday-night scramble? Learn how small churches automate volunteer rotations to save hours every week.',
      twitterCard: 'summary_large_image',
      twitterTitle:
        'How Small Churches Save 3+ Hours a Week by Automating Volunteer Rotations',
      twitterDescription:
        'Tired of the Saturday-night scramble? Learn how small churches automate volunteer rotations to save hours every week.',
    },
    gradient: { from: '#f5f3ff', to: '#ede9fe' },
    icon: '🔄',
    heroGraphic: 'RotationMockupGraphic',
    sections: [
      {
        type: 'p',
        content:
          "It's 9 p.m. on Saturday. You just realized your sound tech texted earlier this week that they can't make it tomorrow. So you start scrolling through your mental rolodex: who knows the board? Who lives close enough? Who won't mind the last-minute ask? You fire off six texts and wait. If you lead a small church, you know this drill by heart.",
      },
      {
        type: 'h2',
        content: 'Why Small Churches Feel This the Hardest',
      },
      {
        type: 'p',
        content:
          "A large church has a dedicated volunteer coordinator and backups for every role. A small church has the same 10 or 15 people covering worship, sound, greeters, children's ministry, and media, often wearing multiple hats in the same service. When one person can't make it, there's nobody on the bench. So it falls on whoever is most willing to scramble.",
      },
      {
        type: 'h2',
        content: "What Manual Rotation Actually Costs You",
      },
      {
        type: 'p',
        content:
          "The time you spend scheduling volunteers isn't just the 45 minutes of texting every week. It's keeping track of who served last week, who said they needed a break, who hasn't been asked in a while. It's going back to the same reliable few because you know they'll say yes. And over time, some volunteers start feeling over-used while others feel forgotten.",
      },
      {
        type: 'ul',
        items: [
          '2–5 hours per week on scheduling texts, calls, and follow-ups',
          'Volunteer fatigue from the same people always being asked',
          'Inconsistent coverage that affects service quality',
          "Pastor burnout from spending energy on logistics instead of people",
          'Last-minute gaps that create stress for your whole team',
        ],
      },
      {
        type: 'h2',
        content: 'What Automated Rotation Looks Like in Practice',
      },
      {
        type: 'p',
        content:
          "With a tool like Scheduly, you set up your rotation once. Define the roles you need each Sunday (worship lead, sound tech, greeters, children's staff) and assign a small pool of volunteers to each one. The system tracks who served last, who's next in line, and when someone marks themselves unavailable, it bumps the next person up and sends them a notification. You get a confirmation back. No chasing.",
      },
      {
        type: 'graphic',
        component: 'RotationMockupGraphic',
      },
      {
        type: 'p',
        content:
          "The difference is night and day. Instead of starting Friday with a blank schedule, you open your dashboard and see a fully staffed Sunday, already confirmed. If something changes, the system flags it and moves to the next person automatically. You're reviewing a schedule, not building one from scratch.",
      },
      {
        type: 'callout',
        content:
          'One worship coordinator told us she went from 45 minutes of weekly scheduling down to about 5. Same team, same Sunday. She just stopped having to chase people.',
      },
      {
        type: 'h2',
        content: 'Three Tips for Getting Started',
      },
      {
        type: 'ol',
        items: [
          "Keep rotations small. Start with 3 or 4 people per role. A bigger pool sounds safer, but people end up serving so rarely they feel disconnected. Three to four keeps everyone sharp without burning anyone out.",
          "Let volunteers mark their own unavailability. Vacations, family events, busy seasons. When people can block off dates themselves instead of texting you last-minute, they stay more engaged and you get fewer surprises.",
          "Review coverage once a week, not once a day. The goal of automation is to get time back, not to create a new thing to monitor. Set a 10-minute block on Thursday morning to confirm the weekend lineup and move on.",
        ],
      },
      {
        type: 'h2',
        content: 'The Real Win: Focus on Ministry, Not Logistics',
      },
      {
        type: 'p',
        content:
          "You will save time by automating rotations. But the bigger thing is what you do with that time. Instead of mentally running through who's missing next Sunday, you're actually talking to people after service. You're checking in with your volunteers instead of just filling slots. That matters more than the hours you get back.",
      },
      {
        type: 'cta',
        heading: 'See What This Looks Like for Your Church',
        content:
          "If you want to try automated rotations with your own volunteer team, Scheduly offers a free 3-month trial. No contracts, no credit card required — just a simpler Sunday.",
        buttonText: 'Start Your Free 3-Month Trial',
        buttonHref: '/checkout?plan=Clubhouse',
      },
    ],
  },
  {
    slug: 'website-first-church-scheduling',
    title:
      'No App Required: How Website-First Scheduling Onboards Your Whole Church',
    date: '2026-03-28',
    author: 'Scheduly Team',
    readTime: '5 min read',
    excerpt:
      'Most scheduling tools ask everyone to download an app. Scheduly takes the opposite approach — and one church onboarded 300 members in weeks.',
    tags: ['Getting Started', 'Church Growth', 'Communication'],
    seo: {
      title:
        'No App Required: Website-First Church Scheduling | Scheduly Blog',
      description:
        'Learn how website-first scheduling lets churches onboard entire congregations without app installs or account creation. See how one church did it in weeks.',
      canonical:
        'https://scheduly.org/blog/website-first-church-scheduling',
      ogTitle:
        'No App Required: How Website-First Scheduling Onboards Your Whole Church',
      ogDescription:
        'Learn how website-first scheduling lets churches onboard entire congregations without app installs or account creation.',
      twitterCard: 'summary_large_image',
      twitterTitle:
        'No App Required: How Website-First Scheduling Onboards Your Whole Church',
      twitterDescription:
        'Learn how website-first scheduling lets churches onboard entire congregations without app installs or account creation.',
    },
    gradient: { from: '#f0fdfa', to: '#ccfbf1' },
    icon: '🌐',
    heroGraphic: 'WebsiteFirstGraphic',
    sections: [
      {
        type: 'p',
        content:
          "You just picked a new scheduling tool for your church. It looks great on the demo. The features check every box. Now comes the hard part: getting 200 members to download an app, create an account, pick a password, and actually log in. You send the email blast, post it in the bulletin, mention it from the pulpit. Two weeks later, you have 30 sign-ups — mostly the same tech-comfortable folks who would have figured it out anyway. The rest of your congregation? Still waiting for someone to walk them through it after service.",
      },
      {
        type: 'h2',
        content: 'Why App-First Onboarding Fails Churches',
      },
      {
        type: 'p',
        content:
          "The typical approach to church software assumes every member will install an app and create an account on their own. For a startup or a tech company, that might work. For a congregation that includes retirees, working parents juggling three kids, and members who still prefer phone calls to texting — it's a wall.",
      },
      {
        type: 'p',
        content:
          "The real cost isn't just low adoption numbers. It's the weeks and months of gentle reminding, one-on-one tech support after service, and the quiet frustration from leaders who picked a tool that was supposed to make things simpler. And the members who never get set up? They're still being scheduled the old way — phone calls and bulletin announcements — which means you're now running two systems instead of one.",
      },
      {
        type: 'ul',
        items: [
          'Half the congregation never completes the sign-up process',
          'Older members and less tech-comfortable people are excluded from day one',
          'Admins spend weeks doing one-on-one tech support instead of ministry',
          'Running the old and new system in parallel doubles the workload',
        ],
      },
      {
        type: 'h2',
        content: 'How Website-First Changes the Equation',
      },
      {
        type: 'p',
        content:
          "Scheduly was built around a simple idea: the admin does the setup, not the member. When a church starts using Scheduly, the volunteer coordinator or pastor creates a member profile for each person — name, phone number, email. That's it. No one in the congregation needs to download anything, create a password, or even know what Scheduly is.",
      },
      {
        type: 'p',
        content:
          "From that moment, when a member is scheduled for a role, they get a personal SMS or email notification. The message tells them what they're scheduled for, when, and gives them a simple way to confirm or decline — tap a link, reply yes or no. The whole interaction takes about ten seconds. No login. No app. No friction.",
      },
      {
        type: 'callout',
        content:
          "Lifeway Baptist Church onboarded all 300 of their members in weeks, not months. Because no one in the congregation needed to create an account, the admin team simply added everyone's contact info and the system started working immediately.",
      },
      {
        type: 'h2',
        content: 'Why This Works for Every Generation',
      },
      {
        type: 'p',
        content:
          "Think about the members in your congregation who are least comfortable with technology. Maybe it's the retired couple who serve faithfully every third Sunday. Maybe it's the single mom who barely has time to check her email, let alone set up another app. A website-first approach means these members are fully included from day one. They receive the same notifications, respond the same way, and stay just as connected as the most tech-savvy person in your church.",
      },
      {
        type: 'p',
        content:
          "This isn't about dumbing things down. It's about removing barriers that don't need to exist. Everyone already has access to the web on their smartphone or desktop. Everyone can receive a text message. Building on what people already have means you skip the onboarding problem entirely.",
      },
      {
        type: 'h2',
        content: 'When Members Want More Access',
      },
      {
        type: 'p',
        content:
          "Some members will eventually want to see the full schedule, check their upcoming commitments, or manage their own availability. Scheduly makes that easy. Any member can sign in through Google or another auth provider and link to the existing profile their admin already created. All their history, contact info, and scheduling data is already there — they just get a front door to it.",
      },
      {
        type: 'p',
        content:
          "This gives you the best of both approaches: zero-friction onboarding for the whole congregation up front, and full self-service access for members who want it later. No one is forced into either path.",
      },
      {
        type: 'h2',
        content: 'How to Roll Out Website-First Scheduling',
      },
      {
        type: 'ol',
        items: [
          "Start with your admin team. Have one or two people enter member names and contact info into Scheduly. For a church of 100–200 members, this usually takes an afternoon. For larger congregations, do it in batches — start with your active volunteers and add the rest over the following week.",
          "Schedule your first event and let the notifications do the talking. Don't announce the tool from the pulpit or ask people to sign up. Just schedule your volunteers and let them receive their first notification naturally. When they get a text saying they're scheduled for greeting on Sunday and can reply YES to confirm, they'll understand immediately.",
          "Offer the sign-in option to those who ask. When members come to you wanting to see their full schedule or mark vacation dates, point them to the sign-in page. Let demand drive adoption of the self-service features rather than pushing everyone through a setup process.",
          "Commit to the system as your single source of truth. The biggest mistake churches make with new tools is running the old system alongside the new one just in case. Members who respond to texts are fully using Scheduly, even if they never log in.",
        ],
      },
      {
        type: 'h2',
        content: 'Meeting People Where They Are',
      },
      {
        type: 'p',
        content:
          "The philosophy behind website-first scheduling is really a ministry philosophy: meet people where they are, not where you wish they were. Not every member of your congregation is going to download an app. Not every volunteer is going to create yet another online account. But every one of them can receive a text and tap \"yes.\" When your scheduling tool works that way from the start, you stop spending energy on adoption and start spending it on the actual work of coordinating, serving, and caring for your community.",
      },
      {
        type: 'cta',
        heading: 'See How Simple Onboarding Can Be',
        content:
          'Add your members, schedule your first event, and let them respond by text — no app installs, no accounts, no tech support. Try it free for three months.',
        buttonText: 'Start Your Free 3-Month Trial',
        buttonHref: '/checkout?plan=Clubhouse',
      },
    ],
  },
];

export default blogPosts;
